import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import FilterBar from '@/pages/category/components/FilterBar';
import { movies } from '@/mocks/movies';

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);

  const activeType = searchParams.get('category') || '';
  const activeGenre = searchParams.get('genre') || '';
  const activeRegion = searchParams.get('region') || '';
  const activeYear = searchParams.get('year') || '';
  const keyword = searchParams.get('keyword') || '';
  const sort = searchParams.get('sort') || 'latest';
  const page = Number(searchParams.get('page')) || 1;

  const toOption = (values: string[]) => [
    { label: '全部', value: '' },
    ...values.map((v) => ({ label: v, value: v })),
  ];

  const types = [
    { label: '全部', value: '' },
    { label: '电影', value: '电影' },
    { label: '电视剧', value: '电视剧' },
  ];
  const genres = useMemo(
    () => toOption(Array.from(new Set(movies.flatMap((m) => m.genres)))),
    []
  );
  const regions = useMemo(() => toOption(Array.from(new Set(movies.map((m) => m.region)))), []);
  const years = useMemo(
    () => toOption(Array.from(new Set(movies.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a))),
    []
  );

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (activeType && m.category !== activeType) return false;
      if (activeGenre && !m.genres.includes(activeGenre)) return false;
      if (activeRegion && m.region !== activeRegion) return false;
      if (activeYear && String(m.year) !== activeYear) return false;
      if (keyword) {
        const hay = [m.title, m.director, ...m.cast, ...m.genres, m.synopsis].join(' ');
        if (!hay.includes(keyword)) return false;
      }
      return true;
    });
    if (sort === 'popular') return [...list].sort((a, b) => b.rating - a.rating);
    return [...list].sort((a, b) => b.year - a.year);
  }, [activeType, activeGenre, activeRegion, activeYear, keyword, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const resetAll = () => setSearchParams(new URLSearchParams());

  const heading = activeType || (keyword ? `搜索“${keyword}”` : '全部作品');
  const subtitle = activeType === '电影'
    ? '精选情感纠葛题材电影，含剧情梗概与演员阵容，可按题材、地区与年代筛选。'
    : activeType === '电视剧'
      ? '精选情感纠葛题材电视剧集，含分集剧情与主演信息，可按题材、地区与年代筛选。'
      : '汇集各类情感纠葛题材电影与电视剧，支持按题材、地区与年代多维筛选。';

  return (
    <>
      <Seo
        title={`${heading} - 奸情影视大全`}
        description={subtitle}
        keywords={`奸情,${heading},剧情介绍,演员,影视分类`}
      />
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        <nav className="mb-5 flex items-center gap-2 text-xs text-foreground-500 md:text-sm" aria-label="面包屑导航">
          <Link to="/" className="transition-colors hover:text-foreground-950">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">影视分类</span>
          {activeType && (
            <>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-950">{activeType}</span>
            </>
          )}
        </nav>

        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {heading} <span className="text-foreground-500">· 剧情大全</span>
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{subtitle}</p>
        </div>

        <FilterBar
          types={types}
          genres={genres}
          regions={regions}
          years={years}
          activeType={activeType}
          activeGenre={activeGenre}
          activeRegion={activeRegion}
          activeYear={activeYear}
          sort={sort}
          keyword={keyword}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          onChange={updateParam}
          onReset={resetAll}
        />

        <p className="mb-5 mt-5 text-sm text-foreground-500">
          共 <span className="font-semibold text-accent-400">{filtered.length}</span> 部作品
        </p>

        {pageItems.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pageItems.map((movie, i) => (
              <div key={movie.id} className="animate-card-in" style={{ animationDelay: `${i * 50}ms` }}>
                <MovieCard movie={movie} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">没有找到符合条件的作品</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              试试减少筛选条件，或清空筛选查看全部作品。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              清空筛选条件
            </button>
          </div>
        )}

        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          onChange={(p) => updateParam('page', String(p))}
        />
      </div>
    </>
  );
}